import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Download, Eye, CheckCircle, XCircle } from 'lucide-react';

import toast from 'react-hot-toast';

import PageHeader from './ui/PageHeader';
import DataTable, { Pagination, TableToolbar } from './ui/DataTable';
import Button from './ui/Button';
import Badge, { statusVariant } from './ui/Badge';
import doctorServices from '../../core/services/doctorServices';
import roomServices from '../../core/services/roomServices';
import sessionServices from '../../core/services/sessionSevices';

export default function Sessions() {
  const history = useHistory();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: '',
    doctor_id: '',
    room_id: '',
    status: '',
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [doctors, setDoctors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { fetchDropdowns(); }, []);
  useEffect(() => { fetchSessions(); }, [filters, page]);

  const fetchDropdowns = async () => {
    try {
      const [d, r] = await Promise.all([
        doctorServices.getDoctors(),
        roomServices.getRoomList(),
      ]);
      setDoctors(d || []);
      setRooms(r || []);
    } catch {}
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const query = { page, limit, ...filters };
      const urlParams = new URLSearchParams(
        Object.fromEntries(
          Object.entries(query).filter(([, v]) => v !== '' && v != null)
        )
      ).toString();
      const { data } = await sessionServices.getSessions({ page, limit, ...filters });
      setSessions(data.data || []);
      setTotal(data.total || data.data?.length || 0);
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status, reason = '') => {
    try {
      await sessionServices.updateSessionStatus(id, status, reason);
      toast.success(
        status === 'Cancelled' ? 'Session cancelled successfully' : 'Session marked completed'
      );
      setCancelModal(null);
      setCancelReason('');
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const isSessionStarted = (s) => {
    if (!s.session_date || !s.start_time) return false;
    const [y, m, d] = s.session_date.split('-').map(Number);
    const [hh, mm] = s.start_time.split(':').map(Number);
    return new Date() >= new Date(y, m - 1, d, hh, mm);
  };

  const setFilter = (k, v) => {
    setFilters((p) => ({ ...p, [k]: v }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: '', date_from: '', date_to: '', doctor_id: '', room_id: '', status: '' });
    setPage(1);
  };

  const exportCsv = () => {
    if (sessions.length === 0) return toast.error('No data to export');
    const headers = ['Date', 'Time', 'Patient Name', 'Phone', 'Doctor', 'Room', 'Status'];
    const csvData = sessions.map((s) => [
      s.session_date,
      `${s.start_time?.slice(0, 5)} - ${s.end_time?.slice(0, 5)}`,
      s.patient_name,
      s.phone,
      s.doctor_name || 'Unassigned',
      s.room_number || 'Unassigned',
      s.status,
    ]);
    const csvContent = [
      headers.join(','),
      ...csvData.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'sessions.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Exported successfully');
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const columns = [
    {
      key: 'patient',
      header: 'Patient',
      render: (s) => (
        <div className="profile-cell">
          <div className="avatar-sm">{(s.patient_name || 'P').charAt(0)}</div>
          <div>
            <div className="profile-cell-title">{s.patient_name || 'No patient'}</div>
            <div className="profile-cell-subtitle">
              {s.pid || 'No ID'}{s.phone ? ` - ${s.phone}` : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'session',
      header: 'Session',
      render: (s) => (
        <div>
          <div className="profile-cell-title">
            {s.session_date || '—'}
          </div>
          <div className="profile-cell-subtitle">
            {s.start_time?.slice(0, 5)} – {s.end_time?.slice(0, 5)}
          </div>
        </div>
      ),
    },
    {
      key: 'doctor',
      header: 'Doctor',
      render: (s) => (
        <div>
          <div className="profile-cell-title">{s.doctor_name || 'Unassigned'}</div>
          <div className="profile-cell-subtitle">{s.room_number || 'No room'}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <Badge variant={statusVariant(s.status)}>{s.status || 'Unknown'}</Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (s) => {
        const started = isSessionStarted(s);
        const normalizedStatus = (s.status || '').toLowerCase();
        return (
          <div className="session-action-buttons">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => history.push(`/sessions/${s.id}`, { record: s })}
            >
              <Eye size={16} />
            </Button>

            {normalizedStatus === 'scheduled' && (
              <>
                <Button
                  variant={started ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={!started}
                  onClick={() =>
                    started
                      ? handleUpdateStatus(s.id, 'Completed')
                      : toast.error(`Cannot complete yet. Starts at ${s.start_time?.slice(0, 5)}`)
                  }
                >
                  <CheckCircle size={16} />
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => { setCancelModal(s); setCancelReason(''); }}
                >
                  <XCircle size={16} />
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="page-container fade-in">
      <PageHeader
        eyebrow="Appointments"
        title="Daily Sessions"
        subtitle="Track therapy sessions across patients, rooms, doctors, and date ranges."
        actions={
          <Button variant="secondary" onClick={exportCsv}>
            <Download size={17} /> Export CSV
          </Button>
        }
      />

      <DataTable
        title="Session Schedule"
        subtitle="Manage completion and cancellation actions."
        loading={loading}
        rows={sessions}
        columns={columns}
        empty="No sessions found."
        toolbar={
          <TableToolbar
            search={filters.search}
            onSearch={(v) => setFilter('search', v)}
            placeholder="Search patient, doctor..."
            filters={
              <div className="filter-row">
                <input
                  type="date"
                  className="form-control"
                  value={filters.date_from}
                  onChange={(e) => setFilter('date_from', e.target.value)}
                  title="From date"
                />
                <input
                  type="date"
                  className="form-control"
                  value={filters.date_to}
                  onChange={(e) => setFilter('date_to', e.target.value)}
                  title="To date"
                />
                <select
                  className="form-control"
                  value={filters.doctor_id}
                  onChange={(e) => setFilter('doctor_id', e.target.value)}
                  title="Filter by doctor"
                >
                  <option value="">All Doctors</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.first_name} {d.last_name}
                    </option>
                  ))}
                </select>
                <select
                  className="form-control"
                  value={filters.room_id}
                  onChange={(e) => setFilter('room_id', e.target.value)}
                  title="Filter by room"
                >
                  <option value="">All Rooms</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.roomName}</option>
                  ))}
                </select>
                <select
                  className="form-control"
                  value={filters.status}
                  onChange={(e) => setFilter('status', e.target.value)}
                  title="Filter by status"
                >
                  <option value="">All Status</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                {hasFilters && (
                  <Button variant="secondary" size="sm" onClick={clearFilters}>
                    Clear
                  </Button>
                )}
              </div>
            }
          />
        }
        footer={
          <Pagination
            page={page}
            total={total}
            limit={limit}
            count={sessions.length}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        }
      />

      {/* CANCEL MODAL */}
      {cancelModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: 'min(480px, 100%)' }}>
            <div className="modal-header">
              <h2>Cancel Session</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setCancelModal(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#64748b', marginBottom: 12 }}>
                Cancelling session for <strong>{cancelModal.patient_name}</strong> on{' '}
                <strong>{cancelModal.session_date}</strong>.
              </p>
              <div className="form-group">
                <label className="form-label">Cancellation Reason</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter reason (optional)"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setCancelModal(null)}>
                Back
              </Button>
              <Button
                variant="danger"
                loading={cancelling}
                onClick={async () => {
                  setCancelling(true);
                  await handleUpdateStatus(cancelModal.id, 'Cancelled', cancelReason);
                  setCancelling(false);
                }}
              >
                Confirm Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Styles are in ./styles/appointments.css */}
    </div>
  );
}